import { intervalLabel } from '@fieldops/shared'
import { useState } from 'react'
import { Icon } from '../Icon.js'
import { PriceInput } from '../proposal/PricingInspector.js'
import { Button, Input } from '../ui.js'
import type { AgreementPage } from './AgreementGrid.js'
import type { ServicePatch } from './AgreementTable.js'
import { commonRule, frequencyKey, frequencyOf } from './agreement-view.js'
import { FREQUENCIES } from './builder-model.js'

type Row = AgreementPage['schedules'][number]
type Mode = 'price' | 'often' | 'first' | 'remove' | null

/**
 * What can be done to every ticked service at once: a price, how often, the
 * first visit, pausing, and taking them off. It floats at the foot of the
 * page while anything is ticked. Removing asks first; jobs already made stay.
 * In Coming up it also makes the jobs the ticked lines are ready for.
 */
export function AgreementBulkBar({
  rows,
  term,
  onPatch,
  onRemove,
  onClear,
  makeJobs,
}: {
  rows: readonly Row[]
  term: { min: string; max: string }
  onPatch: (patch: ServicePatch) => Promise<unknown>
  onRemove: () => Promise<unknown>
  onClear: () => void
  /** The ticked lines ready for a job, and making them. */
  makeJobs?: { count: number; busy: boolean; onMake: () => void }
}) {
  const [mode, setMode] = useState<Mode>(null)
  const [busy, setBusy] = useState(false)
  const [price, setPrice] = useState<number | null>(null)
  const [often, setOften] = useState('')
  const [first, setFirst] = useState('')

  // Each opens on what most of the ticked services already have.
  const open = (next: Mode) => {
    const common = commonRule(rows)
    setPrice(common.priceIncTax)
    setOften(frequencyKey(common))
    setFirst(common.firstDueOn ?? term.min)
    setMode(next)
  }

  const n = rows.length
  const services = `${n} service${n === 1 ? '' : 's'}`
  const severalOnAVisit = rows.some((row) => row.services.length !== 1)
  const anyRunning = rows.some((row) => row.stoppedAt === null)
  const anyStopped = rows.some((row) => row.stoppedAt !== null)

  const run = (work: () => Promise<unknown>) => {
    setBusy(true)
    work()
      .then(() => setMode(null))
      .catch(() => undefined)
      .finally(() => setBusy(false))
  }

  const cancel = (
    <Button size="sm" onClick={() => setMode(null)}>
      Cancel
    </Button>
  )

  return (
    <div
      role="region"
      aria-label="Ticked services"
      className="sticky bottom-[var(--page-pad)] z-20 flex flex-wrap items-center gap-2 rounded-xl bg-white px-4 py-2.5 shadow-lg ring-1 ring-ink-200"
    >
      <span className="text-[13px] font-semibold text-ink-900 tabular-nums">{`${services} ticked`}</span>
      <span aria-hidden="true" className="mx-1 h-5 w-px bg-ink-200" />

      {mode === 'price' ? (
        <>
          <label htmlFor="bulk-price" className="text-[12.5px] text-ink-600">
            Per visit
          </label>
          <PriceInput id="bulk-price" value={price} onChange={setPrice} placeholder="$0.00" className="w-28" />
          <Button size="sm" variant="primary" loading={busy} onClick={() => run(() => onPatch({ priceIncTax: price }))}>
            {`Set the price on ${services}`}
          </Button>
          {cancel}
        </>
      ) : mode === 'often' ? (
        <>
          <label htmlFor="bulk-often" className="text-[12.5px] text-ink-600">
            How often
          </label>
          <select
            id="bulk-often"
            value={often}
            onChange={(event) => setOften(event.target.value)}
            className="h-8 rounded-md border border-ink-200 bg-white px-2 text-[12.5px]"
          >
            {FREQUENCIES.map((option) => (
              <option key={frequencyKey(option)} value={frequencyKey(option)}>
                {intervalLabel(option)}
              </option>
            ))}
          </select>
          <Button size="sm" variant="primary" loading={busy} onClick={() => run(() => onPatch(frequencyOf(often)))}>
            {`Set how often on ${services}`}
          </Button>
          {cancel}
        </>
      ) : mode === 'first' ? (
        <>
          <label htmlFor="bulk-first" className="text-[12.5px] text-ink-600">
            First visit
          </label>
          <Input
            id="bulk-first"
            type="date"
            min={term.min}
            max={term.max}
            value={first}
            onChange={(event) => setFirst(event.target.value)}
            className="h-8 w-40"
          />
          <Button size="sm" variant="primary" loading={busy} disabled={!first} onClick={() => run(() => onPatch({ firstDueOn: first }))}>
            {`Set the first visit on ${services}`}
          </Button>
          {cancel}
        </>
      ) : mode === 'remove' ? (
        <>
          <p role="alert" className="min-w-0 text-[12.5px] text-red-800">
            {`Take ${services} off the agreement? Nothing more is planned for them. Jobs already made stay.`}
          </p>
          <Button size="sm" onClick={() => setMode(null)}>
            Keep them
          </Button>
          <Button size="sm" variant="danger" loading={busy} onClick={() => run(onRemove)}>
            {`Remove ${services}`}
          </Button>
        </>
      ) : (
        <>
          {makeJobs && makeJobs.count > 0 ? (
            <Button
              size="sm"
              variant="primary"
              icon={<Icon name="plus" className="size-3.5" />}
              loading={makeJobs.busy}
              onClick={makeJobs.onMake}
            >
              {`Make ${makeJobs.count} job${makeJobs.count === 1 ? '' : 's'}`}
            </Button>
          ) : null}
          <Button
            size="sm"
            variant="ghost"
            disabled={severalOnAVisit}
            title={severalOnAVisit ? 'A visit of several services is priced in Edit service.' : undefined}
            onClick={() => open('price')}
          >
            Set price
          </Button>
          <Button size="sm" variant="ghost" onClick={() => open('often')}>
            How often
          </Button>
          <Button size="sm" variant="ghost" onClick={() => open('first')}>
            First visit
          </Button>
          {anyRunning ? (
            <Button
              size="sm"
              variant="ghost"
              icon={<Icon name="pause" className="size-3.5" />}
              loading={busy}
              onClick={() => run(() => onPatch({ paused: true }))}
            >
              Pause
            </Button>
          ) : null}
          {anyStopped ? (
            <Button
              size="sm"
              variant="ghost"
              icon={<Icon name="play" className="size-3.5" />}
              loading={busy}
              onClick={() => run(() => onPatch({ paused: false }))}
            >
              Resume
            </Button>
          ) : null}
          <Button
            size="sm"
            variant="ghost"
            className="text-red-700 hover:bg-red-50"
            icon={<Icon name="trash" className="size-3.5" />}
            onClick={() => open('remove')}
          >
            Remove
          </Button>
        </>
      )}

      <button
        type="button"
        onClick={onClear}
        className="ml-auto rounded-md px-1.5 py-1 text-xs font-medium text-ink-500 hover:bg-ink-100 hover:text-ink-800"
      >
        Clear
      </button>
    </div>
  )
}
